"use client";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import {
  getAuth,
  browserLocalPersistence,
  onAuthStateChanged,
  setPersistence,
  signOut,
  signInWithCustomToken,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { app } from "@/config/firebaseConfig";
import useBackendUrl from "@/hooks/useBackendUrl";
import { useAsyncRoutePush } from "@/hooks/useAsyncPush";
import Script from "next/script";
import Cookies from "js-cookie";
import { ensurePersistence } from "@/app/helpers/persistence";

const CACHE_KEY = (mail) => `userData:${mail}`;

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  /* ───────── State ───────── */
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true); // global loader: only while booting / navigation
  const [ready, setReady] = useState(false); // auth listener has fired at least once
  const [error, setError] = useState(null);

  const api = useBackendUrl();
  const push = useAsyncRoutePush();
  const auth = getAuth(app);
  const db = getFirestore(app);

  // ----------------------
  // Cookie consent toggles
  // ----------------------
  const [isEssentialChecked, setIsEssentialChecked] = useState(
    Cookies.get("essential") ? true : true
  );
  const [isAnalyticalChecked, setIsAnalyticalChecked] = useState(
    Cookies.get("analytical") ? true : false
  );
  const [isMarketingChecked, setIsMarketingChecked] = useState(
    Cookies.get("marketing") ? true : false
  );

  /* ───────── Persistence ───────── */
  useEffect(() => {
    ensurePersistence(auth).catch(console.error);
  }, [auth]);

  /* -------------------------------------------------------------------------- */
  /*                            Auth- & Firestore listener                       */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    let unsubSnap; // unsubscribe fn for user doc
    let cachedKey = null; // remember which cache entry we used

    const unsubAuth = onAuthStateChanged(auth, async (cur) => {
      /* ----------------------------- LOGGED OUT ---------------------------- */
      if (!cur) {
        unsubSnap?.(); // stop user-doc stream
        if (cachedKey) sessionStorage.removeItem(cachedKey);
        setUser(null);
        setUserData(null);
        setLoading(false);
        setReady(true);
        return;
      }

      /* ----------------------------- LOGGED IN ----------------------------- */
      const email = cur.email.toLowerCase();
      setUser({ ...cur, email });
      const ref = doc(db, "AuktionUsers", email);
      cachedKey = CACHE_KEY(email);

      /* 1️⃣  Read cache immediately */
      const cached = sessionStorage.getItem(cachedKey);
      if (cached && cached !== "null") {
        setUserData(JSON.parse(cached));
        setLoading(false);
        setReady(true);
      }

      /* 2️⃣  One-shot getDoc */
      let docExists = false;
      try {
        const snap = await getDoc(ref);
        docExists = snap.exists();
        if (docExists) {
          const data = snap.data();
          setUserData(data);
          sessionStorage.setItem(cachedKey, JSON.stringify(data));
          setLoading(false);
          setReady(true);
        }
      } catch (e) {
        console.error(e);
      }

      /* 3️⃣  Create skeleton doc if none */
      if (!docExists) {
        const base = {
          createdAt: serverTimestamp(),
          mailTags: { online: true },
        };
        await setDoc(ref, base, { merge: true });
        setUserData(base);
        sessionStorage.setItem(cachedKey, JSON.stringify(base));
        setLoading(false);
        setReady(true);
      }

      /* 4️⃣  Live updates */
      unsubSnap?.(); // safety – close previous
      unsubSnap = onSnapshot(ref, (s) => {
        if (!s.exists()) return;
        const data = s.data();
        setUserData(data);
        sessionStorage.setItem(cachedKey, JSON.stringify(data));
        if (loading) {
          setLoading(false);
          setReady(true);
        }
      });

      /* 5️⃣  Update online flag */
      setDoc(
        ref,
        { lastOnline: serverTimestamp(), mailTags: { online: true } },
        { merge: true }
      ).catch(console.error);
    });

    return () => {
      unsubAuth();
      unsubSnap?.();
    };
  }, [auth, db, api]);

  /* -------------------------------------------------------------------------- */
  /*                               Login / Logout                               */
  /* -------------------------------------------------------------------------- */
  const login = async (mail, pw, link = false) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${api}/auction/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: mail.toLowerCase(),
          password: link ? undefined : pw,
          loginWithEmailLink: link,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Login failed");

      if (!link) {
        await signInWithCustomToken(auth, data.token); // Firebase-Session setzen
        await push("/marktplatz"); // Navigation
      } else {
        // Link-Variante war schon okay
      }

      return { success: true };
    } catch (e) {
      setError(e.message);
      return { success: false, error: e };
    } finally {
      // Egal ob success oder Fehler: Lade-State beenden
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await push("/");
      await signOut(auth); // fires auth listener → cur === null
      sessionStorage.clear(); // defensive: nuke all cached user data
      setUser(null);
      setUserData(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                 Context                                    */
  /* -------------------------------------------------------------------------- */
  const ctx = useMemo(
    () => ({
      user,
      userData,
      loading,
      authInitialized: ready,
      error,
      isLoggedIn: !!user,
      login,
      logout,
    }),
    [user, userData, loading, ready, error]
  );

  /* -------------------------------------------------------------------------- */
  /*                             Provider & scripts                              */
  /* -------------------------------------------------------------------------- */
  return (
    <AuthContext.Provider value={ctx}>
      {isMarketingChecked && (
        <Script
          id="fb-pixel"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `!function(f,b,e,v,n,t,s){if(f.fbq)return;
            n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;
            s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
            (window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
            fbq('init','645534931725309');fbq('track','PageView');`,
          }}
        />
      )}

      {/* Microsoft Clarity – NUR EINMAL EINBETTEN */}

      {isAnalyticalChecked && (
        <Script
          id="clarity"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `(function(c,l,a,r,i,t,y){if(c.clarity)return;
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src=
            "https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "r78cayvlow");`,
          }}
        />
      )}

      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
